import {
  useEffect,
  useRef,
  useState
} from 'react'
import { useDispatch } from 'react-redux'
import {
  Box,
  Button,
  Flex,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react'
import {
  AddImageIcon, CloseIcon, MicIcon, PlusSignIcon, TrashIcon
} from 'assets/icons'
import { getNotificationMessage } from 'store'
import { sendMessage } from 'store/chat/services'
import { REJECTED } from 'utils/constants'
import { isMobile } from 'utils/helpers'
import { COLORS } from 'utils/styles'

import { DropupMenu } from './DropupMenu'

export const InputBar = ({
  roomId,
  hasUnsecuredMember,
  replyToEventId,
  setReplyToEventId
}) => {
  const dispatch = useDispatch()
  const [draft, setDraft] = useState('')
  const textareaRef = useRef(null)

  const [recording, setRecording] = useState(false)
  const [audioUrl, setAudioUrl] = useState(null)
  const recorderRef = useRef(null)
  const chunksRef = useRef([])

  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    const recorder = new MediaRecorder(stream)
    chunksRef.current = []
    recorder.ondataavailable = (e) => chunksRef.current.push(e.data)
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType })
      setAudioUrl(URL.createObjectURL(blob))
      stream.getTracks().forEach((t) => t.stop())
    }
    recorderRef.current = recorder
    recorder.start()
    setRecording(true)
  }

  const stop = () => {
    recorderRef.current?.stop()
    setRecording(false)
  }

  const resize = () => {
    const el = textareaRef.current
    if (!el) {
      return
    }

    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`

    if (el.scrollHeight <= el.clientHeight) {
      el.scrollTop = 0
    }
  }

  const handleChange = (e) => {
    setDraft(e.target.value)
    resize()
  }

  const handleSend = async () => {
    const text = draft.trim()
    const tempReplyToEventId = replyToEventId
    if (!text || hasUnsecuredMember) {
      return
    }

    setDraft('')
    setReplyToEventId(null)
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.scrollTop = 0
    }

    const result = await dispatch(sendMessage({
      roomId,
      text,
      replyToEventId
    }))

    if (sendMessage.rejected.match(result)) {
      setDraft((current) => current || text)
      setReplyToEventId((current) => current || tempReplyToEventId)
      requestAnimationFrame(resize)
      dispatch(getNotificationMessage({ type: REJECTED, message: 'Message failed to send' }))
    }
  }

  const handleKeyDown = (e) => {
    if (
      e.key === 'Enter'
      && !e.shiftKey
      && !e.nativeEvent.isComposing
    ) {
      e.preventDefault()
      handleSend()
    }
  }

  useEffect(() => {
    if (replyToEventId) {
      textareaRef.current.focus()
    }
  }, [replyToEventId])

  return (
    <Flex
      align="center"
      gap={3}
      px={3}
      py="8px"
      borderTop="1px solid"
      borderColor="whiteAlpha.300"
    >
      {/* <Box boxSize="48px" flexShrink={0} /> */}

      <Box flex={1}>
        {
          replyToEventId
            ? (
              <Flex
                width="100%"
                justify="space-between"
                align="center"
              >
                <Text marginBottom="7px">Replying</Text>

                <Box
                  paddingBottom="7px"
                  cursor="pointer"
                  onClick={() => setReplyToEventId(null)}
                >
                  <CloseIcon fill={COLORS.lightGray} />
                </Box>
              </Flex>
            )
            : ''
        }
        {/* <Stack display="flex" flexDirection="row"
          width="100%" alignItems="center" gap={3} marginBottom="7px">
          <Box width="40px" height="40px" justifyContent="center"
          alignItems="center" display="flex">
            <Box cursor="pointer" backgroundColor="red" width="35px"
            height="35px" justifyContent="center" alignItems="center"
            borderRadius="full" display="flex">
              <TrashIcon color="black" width="24px" height="24px" />
            </Box>
          </Box>
          <audio id="audio-playback" style={{ width: isMobile() ?
          '80%' : '320px' }} src={audioUrl} controls>
            <track src="captions.vtt" kind="captions" srcLang="en" label="English" />
            Your browser does not support the audio element.
          </audio>
          <Box backgroundColor="white" color="black" width="40px"
          height="40px" justifyContent="center" alignItems="center" borderRadius="full">
            {recording ? 'Stop' : 'Record'}
          </Box>
        </Stack> */}

        <Box display="flex" width="100%" flexDirection="row" justifyContent="middle" alignItems="center" gap={3}>
          <Box
            cursor="pointer"
            backgroundColor="transparent"
            _hover={{ backgroundColor: 'transparent' }}
            borderRadius="full"
            onClick={(e) => {}}
          >
            <AddImageIcon width="40px" height="40px" />
          </Box>
          <Box
            flex="1"
            px={6}
            py="11px"
            width={isMobile() ? '80%' : '320px'}
            maxWidth="320px"
            border="1px solid"
            borderColor="whiteAlpha.500"
            borderRadius="3xl"
            cursor="text"
            overflow="hidden"
            _hover={{ borderColor: 'whiteAlpha.600' }}
            _focusWithin={{ borderColor: 'gray.300' }}
            onClick={() => textareaRef.current?.focus()}
          >
            <Textarea
              ref={textareaRef}
              value={draft}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Message..."
              rows={1}
              resize="none"
              display="block"
              minH="0"
              maxH="120px"
              p={0}
              border="none"
              borderRadius="0"
              bg="transparent"
              fontSize="18px"
              lineHeight="24px"
              color={COLORS.lightGray}
              _placeholder={{ color: 'gray.400' }}
              _focusVisible={{ boxShadow: 'none' }}
              sx={{
                fieldSizing: 'content',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none'
                }
              }}
            />
          </Box>
          <Box width="40px">
            <Box height="30px" width="30px" display="flex" marginRight="20px" opacity="75%">
              <MicIcon />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* <Box boxSize="48px" flexShrink={0} /> */}
    </Flex>
  )
}
